import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useApi } from '../../api/api-context';
import { useBudgetId } from '../budget-store';
import { useHistoryStore } from '../../history/history-store';
import { toDollars, toPennies } from '../../utils/format';
export const useMoveAssignmentApi = (options) => {
    const api = useApi();
    const queryClient = useQueryClient();
    const budgetId = useBudgetId();
    const historyStore = useHistoryStore();
    const [isPending, setIsPending] = useState(false);
    const bucketsKey = ['buckets', budgetId];
    const accountsKey = ['accounts', budgetId];
    const moveAssignmentApi = async (data) => {
        await api.post('/budget/assignment/move', data);
    };
    const mutation = useMutation({
        mutationFn: moveAssignmentApi,
        onMutate: async (request) => {
            setIsPending(true);
            // Cancel any outgoing queries to avoid stale data
            await queryClient.cancelQueries({ queryKey: bucketsKey });
            await queryClient.cancelQueries({ queryKey: accountsKey });
            // Snapshot the previous cache state for rollback if needed
            const previousBuckets = queryClient.getQueryData(bucketsKey);
            const previousAccounts = queryClient.getQueryData(accountsKey);
            const amountInPennies = toPennies(request.amount);
            // Handle source (always update if sourceId exists)
            if (request.sourceId) {
                queryClient.setQueryData(bucketsKey, (oldBuckets) => {
                    if (!oldBuckets)
                        return undefined;
                    return oldBuckets.map((bucket) => {
                        if (bucket.id === request.sourceId) {
                            const existingAssignIndex = bucket.assignments.findIndex((assign) => assign.year === request.year && assign.month === request.month);
                            let newAssignments;
                            if (existingAssignIndex !== -1) {
                                // Assignment exists, subtract amount
                                newAssignments = [...bucket.assignments];
                                const assign = newAssignments[existingAssignIndex];
                                const currentPennies = toPennies(assign.amount);
                                const newAmount = toDollars(currentPennies - amountInPennies);
                                newAssignments[existingAssignIndex] = { ...assign, amount: newAmount };
                            }
                            else {
                                // Assignment doesn't exist, create it with negative amount
                                newAssignments = [
                                    ...bucket.assignments,
                                    { year: request.year, month: request.month, amount: -request.amount },
                                ];
                            }
                            return { ...bucket, assignments: newAssignments };
                        }
                        return bucket;
                    });
                });
                queryClient.setQueryData(accountsKey, (oldAccounts) => {
                    if (!oldAccounts)
                        return undefined;
                    return oldAccounts.map((account) => {
                        if (account.id === request.sourceId) {
                            const existingAssignIndex = account.assignments.findIndex((assign) => assign.year === request.year && assign.month === request.month);
                            let newAssignments;
                            if (existingAssignIndex !== -1) {
                                // Assignment exists, subtract amount
                                newAssignments = [...account.assignments];
                                const assign = newAssignments[existingAssignIndex];
                                const currentPennies = toPennies(assign.amount);
                                const newAmount = toDollars(currentPennies - amountInPennies);
                                newAssignments[existingAssignIndex] = { ...assign, amount: newAmount };
                            }
                            else {
                                // Assignment doesn't exist, create it with negative amount
                                newAssignments = [
                                    ...account.assignments,
                                    { year: request.year, month: request.month, amount: -request.amount },
                                ];
                            }
                            return { ...account, assignments: newAssignments };
                        }
                        return account;
                    });
                });
            }
            // Handle target (if exists)
            if (request.targetId) {
                queryClient.setQueryData(bucketsKey, (oldBuckets) => {
                    if (!oldBuckets)
                        return undefined;
                    return oldBuckets.map((bucket) => {
                        if (bucket.id === request.targetId) {
                            const existingAssignIndex = bucket.assignments.findIndex((assign) => assign.year === request.year && assign.month === request.month);
                            let newAssignments;
                            if (existingAssignIndex !== -1) {
                                newAssignments = [...bucket.assignments];
                                const assign = newAssignments[existingAssignIndex];
                                const currentPennies = toPennies(assign.amount);
                                const newAmount = toDollars(currentPennies + amountInPennies);
                                newAssignments[existingAssignIndex] = {
                                    ...newAssignments[existingAssignIndex],
                                    amount: newAmount,
                                };
                            }
                            else {
                                newAssignments = [
                                    ...bucket.assignments,
                                    { year: request.year, month: request.month, amount: request.amount },
                                ];
                            }
                            return { ...bucket, assignments: newAssignments };
                        }
                        return bucket;
                    });
                });
                queryClient.setQueryData(accountsKey, (oldAccounts) => {
                    if (!oldAccounts)
                        return undefined;
                    return oldAccounts.map((account) => {
                        if (account.id === request.targetId) {
                            const existingAssignIndex = account.assignments.findIndex((assign) => assign.year === request.year && assign.month === request.month);
                            let newAssignments;
                            if (existingAssignIndex !== -1) {
                                newAssignments = [...account.assignments];
                                const assign = newAssignments[existingAssignIndex];
                                const currentPennies = toPennies(assign.amount);
                                const newAmount = toDollars(currentPennies + amountInPennies);
                                newAssignments[existingAssignIndex] = {
                                    ...newAssignments[existingAssignIndex],
                                    amount: newAmount,
                                };
                            }
                            else {
                                newAssignments = [
                                    ...account.assignments,
                                    { year: request.year, month: request.month, amount: request.amount },
                                ];
                            }
                            return { ...account, assignments: newAssignments };
                        }
                        return account;
                    });
                });
            }
            // Call onSuccess immediately after the optimistic update
            if (options?.onSuccess) {
                options.onSuccess();
            }
            return { buckets: previousBuckets, accounts: previousAccounts };
        },
        onError: (_error, _variables, context) => {
            // Rollback to the previous state if the mutation fails
            if (context?.buckets !== undefined) {
                queryClient.setQueryData(bucketsKey, context.buckets);
            }
            if (context?.accounts !== undefined) {
                queryClient.setQueryData(accountsKey, context.accounts);
            }
        },
        onSettled: () => {
            setIsPending(false);
        },
    });
    const mutateWithHistory = async (request) => {
        // Create reverse move request
        const reverseRequest = {
            ...request,
            sourceId: request.targetId,
            targetId: request.sourceId,
            amount: request.amount,
            year: request.year,
            month: request.month,
        };
        // Perform the move
        mutation.mutate(request);
        // Push the history event
        historyStore.pushEvent({
            description: `Move Assignment`,
            undo: () => {
                // Move the amount back to its original location
                mutation.mutate(reverseRequest);
            },
            redo: () => {
                // Move the amount to its new location again
                mutation.mutate(request);
            },
        });
    };
    return {
        mutate: mutateWithHistory,
        isPending,
    };
};
